/**
 * Admin service client — typed API calls for all admin features:
 * Settings, Credentials, Health, Errors, Login Audit.
 *
 * Requirements: 49.1–49.9, 50.4–50.5, 52.1–52.9, 53.6–53.12, 56.1–56.20
 */

import { apiClient } from './apiClient';

// ─── Settings Types ────────────────────────────────────────────────────

export interface ApplicationSetting {
  settingKey: string;
  category: string;
  displayName: string;
  description: string | null;
  currentValue: string;
  defaultValue: string;
  valueType: 'string' | 'number' | 'boolean' | 'json';
  lastModifiedBy: string | null;
  lastModifiedAt: string | null;
}

export interface SettingsByCategory {
  category: string;
  settings: ApplicationSetting[];
}

// ─── Credential Types ──────────────────────────────────────────────────

export interface CredentialField {
  fieldName: string;
  maskedValue: string;
  hasValue: boolean;
}

export interface CredentialEntry {
  integrationId: string;
  integrationName: string;
  fields: CredentialField[];
  configuredCount: number;
  totalFields: number;
  status: 'Configured' | 'Partial' | 'Not Configured';
  lastTestDate: string | null;
  lastTestResult: 'success' | 'failure' | null;
}

export interface TestConnectionResult {
  integrationId: string;
  success: boolean;
  message: string;
  testedAt: string;
}

// ─── Health Types ──────────────────────────────────────────────────────

export type HealthStatus = 'Green' | 'Yellow' | 'Red';

export interface ProcessHealthStatus {
  processId: string;
  processName: string;
  status: HealthStatus;
  lastRunTime: string | null;
  lastStatus: 'success' | 'failure' | null;
  nextScheduledRun: string | null;
  recordsProcessed: number | null;
  errors: number | null;
  durationMs: number | null;
  scheduleMinutes: number;
  yellowThresholdMinutes: number;
  redThresholdMinutes: number;
}

export interface ProcessRunEntry {
  timestamp: string;
  status: 'success' | 'failure';
  recordsProcessed: number | null;
  errors: number | null;
  durationMs: number | null;
  description: string | null;
}

export interface ProcessHealthDetail {
  status: ProcessHealthStatus;
  history: ProcessRunEntry[];
}

// ─── Error Log Types ───────────────────────────────────────────────────

export interface ErrorLogEntry {
  errorId: number;
  timestamp: string;
  errorCode: string | null;
  message: string | null;
  stackTrace: string | null;
  requestContext: string | null;
  userId: string | null;
}

export interface ErrorLogResult {
  entries: ErrorLogEntry[];
  total: number;
}

export interface ErrorLogFilter {
  dateFrom?: string;
  dateTo?: string;
  errorCode?: string;
  userId?: string;
  entityType?: string;
  errorId?: number;
  search?: string;
  limit?: number;
  offset?: number;
}

// ─── Login Audit Types ─────────────────────────────────────────────────

export interface LoginAuditLogEntry {
  loginAuditId: number;
  timestamp: string;
  userId: string | null;
  eventType: string;
  ipAddress: string | null;
  userAgent: string | null;
  authMethod: string | null;
  failureReason: string | null;
  sessionDuration: number | null;
}

export interface LoginAuditResult {
  entries: LoginAuditLogEntry[];
  total: number;
}

export interface LoginAuditFilter {
  dateFrom?: string;
  dateTo?: string;
  userId?: string;
  eventType?: string;
  ipAddress?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

// ─── Settings API ──────────────────────────────────────────────────────

async function getSettings(): Promise<SettingsByCategory[]> {
  return apiClient.get<SettingsByCategory[]>('mgmt/settings');
}

async function getSettingsByCategory(category: string): Promise<ApplicationSetting[]> {
  return apiClient.get<ApplicationSetting[]>(`mgmt/settings/${encodeURIComponent(category)}`);
}

async function updateSetting(key: string, value: string): Promise<ApplicationSetting> {
  return apiClient.put<ApplicationSetting>(`mgmt/settings/${encodeURIComponent(key)}`, { value });
}

async function resetSetting(key: string): Promise<ApplicationSetting> {
  return apiClient.post<ApplicationSetting>(`mgmt/settings/${encodeURIComponent(key)}/reset`);
}

// ─── Credentials API ───────────────────────────────────────────────────

async function getCredentials(): Promise<CredentialEntry[]> {
  return apiClient.get<CredentialEntry[]>('mgmt/credentials');
}

async function updateCredentials(integrationId: string, fields: Record<string, string>): Promise<void> {
  await apiClient.put(`mgmt/credentials/${integrationId}`, { fields });
}

async function testCredentialConnection(integrationId: string): Promise<TestConnectionResult> {
  return apiClient.post<TestConnectionResult>(`mgmt/credentials/${integrationId}/test`);
}

// ─── Health API ────────────────────────────────────────────────────────

async function getHealth(): Promise<ProcessHealthStatus[]> {
  return apiClient.get<ProcessHealthStatus[]>('mgmt/health');
}

async function getProcessHealth(processId: string): Promise<ProcessHealthDetail> {
  return apiClient.get<ProcessHealthDetail>(`mgmt/health/${processId}`);
}

async function triggerRunNow(processId: string): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>(`mgmt/health/${processId}/run-now`);
}

// ─── Error Log API ─────────────────────────────────────────────────────

async function getErrors(filter?: ErrorLogFilter): Promise<ErrorLogResult> {
  const params: Record<string, string | number | boolean | undefined> = {};
  if (filter?.dateFrom) params.dateFrom = filter.dateFrom;
  if (filter?.dateTo) params.dateTo = filter.dateTo;
  if (filter?.errorCode) params.errorCode = filter.errorCode;
  if (filter?.userId) params.userId = filter.userId;
  if (filter?.entityType) params.entityType = filter.entityType;
  if (filter?.errorId) params.errorId = filter.errorId;
  if (filter?.search) params.search = filter.search;
  if (filter?.limit) params.limit = filter.limit;
  if (filter?.offset) params.offset = filter.offset;

  return apiClient.get<ErrorLogResult>('mgmt/errors', params);
}

// ─── Login Audit API ───────────────────────────────────────────────────

async function getLoginAudit(filter?: LoginAuditFilter): Promise<LoginAuditResult> {
  const params: Record<string, string | number | boolean | undefined> = {};
  if (filter?.dateFrom) params.dateFrom = filter.dateFrom;
  if (filter?.dateTo) params.dateTo = filter.dateTo;
  if (filter?.userId) params.userId = filter.userId;
  if (filter?.eventType) params.eventType = filter.eventType;
  if (filter?.ipAddress) params.ipAddress = filter.ipAddress;
  if (filter?.search) params.search = filter.search;
  if (filter?.limit) params.limit = filter.limit;
  if (filter?.offset) params.offset = filter.offset;

  return apiClient.get<LoginAuditResult>('mgmt/login-audit', params);
}

// ─── Export ────────────────────────────────────────────────────────────

export const adminService = {
  // Settings
  getSettings,
  getSettingsByCategory,
  updateSetting,
  resetSetting,
  // Credentials
  getCredentials,
  updateCredentials,
  testCredentialConnection,
  // Health
  getHealth,
  getProcessHealth,
  triggerRunNow,
  // Errors
  getErrors,
  // Login Audit
  getLoginAudit,
};
